import { ArrowUpRightIcon } from '@heroicons/react/20/solid';
import { ArrowUpOnSquareIcon } from '@heroicons/react/24/outline';
import { Row, RowData, type Table as TsTable } from '@tanstack/react-table';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTailwindBreakpoint } from '../../hooks/useTailwindBreakpoint';
import { FileRouteTypes } from '../../routeTree.gen';
import { Button } from '../catalyst-ui-kit/button';
import { Link } from '../catalyst-ui-kit/link';
import { AccordionList } from './AccordionList';
import { DesktopTable } from './DesktopTable';
import { Pagination } from './TableCore';

declare module '@tanstack/table-core' {
  interface TableMeta<TData extends RowData> {
    getRowClassName?: (row: Row<TData>) => string;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    cellHeaderContainerClassName?: string;
    cellHeaderClassname?: string;
    cellContainerClassName?: string;
    cellClassname?: string;
    align?: 'left' | 'right';
    // Mobile accordion metadata
    accordion?: {
      // Show in the trigger (header) - if not set, goes to expandable content
      showInTrigger?: boolean;
      // Hide completely on mobile
      hidden?: boolean;
      // Mark column as action (buttons, etc.) for mobile layout
      isActionColumn?: boolean;
    };
  }
}

export type StateTableProps<T> = {
  table: TsTable<T>;
  isPending: boolean;
  isConnected: boolean;
  isFetching?: boolean;
  isError?: boolean;
  noDataText?: string;
  skeletonRows?: number;
  onRowClick?: (row: T) => void;
  tableHeaderClassname?: string;
  skeletonRowClassName?: string;
  requireConnected?: boolean;
  ref?: React.RefObject<HTMLDivElement | null>;

  // Optional pagination controls - defaults to true when pagination is available
  showPaginationControls?: boolean;
  // When `paginationKey` changes, the pagination is reset to 0
  paginationKey?: unknown;
  fetchNextPage?: () => void;
  viewAllLink?: FileRouteTypes['fullPaths'];
  exportCallback?: () => void;
  pageSize?: number;
  pageSizeOptions?: number[];
  setPageSize?: (pageSize: number) => void;
  className?: string;
  style?: React.CSSProperties;
  hasActions?: boolean;
  mobileLayout?: 'accordion' | 'table';
};

export const StateTable = <T,>({
  table,
  isPending,
  isConnected,
  isFetching,
  isError,
  noDataText = 'No data available',
  skeletonRows = 4,
  onRowClick,
  tableHeaderClassname,
  skeletonRowClassName,
  requireConnected = true,
  showPaginationControls,
  paginationKey,
  fetchNextPage,
  viewAllLink,
  exportCallback,
  pageSize,
  pageSizeOptions,
  setPageSize,
  className,
  style,
  ref,
  hasActions = false,
  mobileLayout = 'accordion',
}: StateTableProps<T>) => {
  const isLg = useTailwindBreakpoint('lg');
  const showTableOnMobile = mobileLayout === 'table';

  const pagination = table.getState().pagination;
  const sorting = table.getState().sorting;

  const rowCount = table.getRowCount();
  // Check if we need to fetch more data based on pagination state
  useEffect(() => {
    const { pageIndex, pageSize } = pagination;
    const itemsNeeded = (pageIndex + 2) * pageSize; // +2 for current page and next page

    // If we're approaching the end of our current data, fetch more
    if (fetchNextPage && itemsNeeded > rowCount) {
      fetchNextPage();
    }
  }, [rowCount, table, fetchNextPage, pagination, sorting]);

  useEffect(() => {
    table.firstPage();
  }, [paginationKey, table]);

  // Update table pagination state when pageSize updates. Having the parent
  // set this in `useReactTable`'s state would require it to also control the
  // page index, so we set it here instead.
  useEffect(() => {
    if (pageSize !== undefined) {
      table.setPagination((currentPagination) => ({
        ...currentPagination,
        pageSize,
        pageIndex: 0,
      }));
    }
  }, [table, pageSize]);

  // Check if pagination is enabled in table
  const isPaginationEnabled = table.getState().pagination !== undefined;

  // Get pagination state from TanStack Table
  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;
  const canNextPage = table.getCanNextPage();
  const canPreviousPage = table.getCanPreviousPage();

  // Determine if pagination controls should be shown
  // Show if explicitly set to true OR if enabled and has multiple pages (and not explicitly false)
  const shouldShowPaginationControls =
    showPaginationControls === true ||
    (showPaginationControls !== false && isPaginationEnabled && pageCount > 1);

  return (
    <div className={twMerge('flex flex-col', className)} style={style}>
      {showTableOnMobile || isLg ? (
        <DesktopTable
          ref={ref}
          table={table}
          isError={isError}
          isPending={isPending}
          hasActions={hasActions}
          isFetching={isFetching}
          noDataText={noDataText}
          onRowClick={onRowClick}
          isConnected={isConnected}
          skeletonRows={skeletonRows}
          requireConnected={requireConnected}
          tableHeaderClassname={tableHeaderClassname}
          skeletonRowClassName={skeletonRowClassName}
        />
      ) : (
        <AccordionList
          ref={ref}
          table={table}
          isError={isError}
          isPending={isPending}
          isFetching={isFetching}
          onRowClick={onRowClick}
          noDataText={noDataText}
          hasActions={hasActions}
          isConnected={isConnected}
          skeletonRows={skeletonRows}
          requireConnected={requireConnected}
        />
      )}

      {shouldShowPaginationControls && !isPending && (
        <Pagination
          currentPage={currentPage}
          onFirst={() => table.firstPage()}
          onNext={() => table.nextPage()}
          onPrevious={() => table.previousPage()}
          hasNextPage={canNextPage}
          hasPreviousPage={canPreviousPage}
          isLoading={isFetching}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          setPageSize={setPageSize}
        />
      )}

      {(canNextPage && viewAllLink) || exportCallback ? (
        <div className="flex w-full items-center justify-between px-2 py-1">
          {!shouldShowPaginationControls && canNextPage && viewAllLink ? (
            <Link to={viewAllLink}>
              <Button text size="2xs" color="secondary">
                View All <ArrowUpRightIcon />
              </Button>
            </Link>
          ) : null}
          {exportCallback ? (
            <Button
              text
              size="sm"
              color="secondary"
              onClick={exportCallback}
              className="flex items-center gap-1"
            >
              Export <ArrowUpOnSquareIcon className="inline size-4" />
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
